import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  ChevronLeft, CheckCircle2, Clock, XCircle, AlertTriangle,
  FileText, DollarSign, Wrench, Building2, User, Download,
  Plus, X, ChevronDown, ChevronUp,
} from "lucide-react";

const STATUS_LABELS = {
  solicitada: "Solicitada",
  cotizando: "Buscando cotizaciones",
  cotizaciones_subidas: "Cotizaciones listas",
  cotizacion_aprobada: "Cotizacion aprobada",
  pago_solicitado: "Pago solicitado",
  pago_aprobado: "Pago aprobado",
  en_tesoreria: "En tesoreria",
  completada: "Completada",
  rechazada: "Rechazada",
  cancelada: "Cancelada",
};

const STATUS_COLORS = {
  solicitada: "bg-slate-100 text-slate-700",
  cotizando: "bg-blue-100 text-blue-700",
  cotizaciones_subidas: "bg-yellow-100 text-yellow-800",
  cotizacion_aprobada: "bg-purple-100 text-purple-700",
  pago_solicitado: "bg-orange-100 text-orange-700",
  pago_aprobado: "bg-green-100 text-green-700",
  en_tesoreria: "bg-teal-100 text-teal-700",
  completada: "bg-emerald-100 text-emerald-700",
  rechazada: "bg-red-100 text-red-700",
  cancelada: "bg-slate-100 text-slate-500",
};

const TIMELINE_STEPS = [
  "solicitada", "cotizando", "cotizaciones_subidas", "cotizacion_aprobada",
  "pago_solicitado", "pago_aprobado", "en_tesoreria", "completada",
];

function fmtDate(iso) {
  if (!iso) return "--";
  try {
    return new Date(iso).toLocaleString("es-MX", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

function fmtMXN(v) {
  if (v == null) return "--";
  return new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(v);
}

function SectionCard({ title, children }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4">
      <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wide mb-3">{title}</h3>
      {children}
    </div>
  );
}

function toBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function OpsMantenimientoDetalle() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [req, setReq] = useState(null);
  const [loading, setLoading] = useState(true);
  const [suppliers, setSuppliers] = useState([]);
  const [showAddQuotation, setShowAddQuotation] = useState(false);
  const [showCompletePayment, setShowCompletePayment] = useState(false);
  const [showReject, setShowReject] = useState(false);
  const [showCancel, setShowCancel] = useState(false);
  const [showApproveQuotation, setShowApproveQuotation] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  // Quotation form
  const [qForm, setQForm] = useState({ supplier_id: "", amount_mxn: "", valid_until: "", pdf: null, pdfB64: "", notes: "" });
  // Complete payment form
  const [payForm, setPayForm] = useState({ method: "transferencia", reference: "", receipt: null, receiptB64: "", send: true });
  // Reject form
  const [rejectReason, setRejectReason] = useState("");
  // Cancel form
  const [cancelReason, setCancelReason] = useState("");
  // Approve quotation
  const [approveQId, setApproveQId] = useState("");
  const [approveNotes, setApproveNotes] = useState("");

  const loadRequest = useCallback(async () => {
    try {
      const res = await api.get(`/ops/maintenance/requests/${id}`);
      setReq(res.data);
    } catch (e) {
      toast.error("No se pudo cargar la solicitud");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { loadRequest(); }, [loadRequest]);

  useEffect(() => {
    if (["mantenimiento", "corporate_admin"].includes(user?.role)) {
      api.get("/ops/maintenance/suppliers", { params: { active: true } })
        .then(r => setSuppliers(r.data || []))
        .catch(() => {});
    }
  }, [user]);

  const role = user?.role;
  const isManager = ["mantenimiento"].includes(role);
  const isApprover = ["direccion", "corporate_admin"].includes(role);
  const isTreasury = ["treasury", "corporate_admin"].includes(role);
  const isRequester = req?.requested_by_user_id === user?.id;

  async function doAction(path, payload = {}) {
    setActionLoading(true);
    try {
      await api.post(`/ops/maintenance/requests/${id}/${path}`, payload);
      toast.success("Accion completada");
      await loadRequest();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al ejecutar la accion");
    } finally {
      setActionLoading(false);
    }
  }

  async function submitQuotation() {
    if (!qForm.supplier_id || !qForm.amount_mxn || !qForm.valid_until || !qForm.pdfB64) {
      toast.error("Completa todos los campos obligatorios");
      return;
    }
    setActionLoading(true);
    try {
      await api.post(`/ops/maintenance/requests/${id}/quotations`, {
        supplier_id: qForm.supplier_id,
        amount_mxn: parseFloat(qForm.amount_mxn),
        valid_until: qForm.valid_until,
        pdf_base64: qForm.pdfB64,
        notes: qForm.notes || null,
      });
      toast.success("Cotizacion agregada");
      setShowAddQuotation(false);
      setQForm({ supplier_id: "", amount_mxn: "", valid_until: "", pdf: null, pdfB64: "", notes: "" });
      await loadRequest();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al agregar cotizacion");
    } finally {
      setActionLoading(false);
    }
  }

  async function submitCompletePayment() {
    if (!payForm.receiptB64) { toast.error("El comprobante es obligatorio"); return; }
    setActionLoading(true);
    try {
      await api.post(`/ops/maintenance/requests/${id}/complete-payment`, {
        payment_method: payForm.method,
        payment_reference: payForm.reference || null,
        payment_receipt_base64: payForm.receiptB64,
        send_to_supplier: payForm.send,
      });
      toast.success("Pago completado");
      setShowCompletePayment(false);
      await loadRequest();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al completar el pago");
    } finally {
      setActionLoading(false);
    }
  }

  async function submitApproveQuotation() {
    if (!approveQId) { toast.error("Elige una cotizacion"); return; }
    await doAction("approve-quotation", { quotation_id: approveQId, notes: approveNotes || null });
    setShowApproveQuotation(false);
  }

  async function handleQPdf(e) {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error("El PDF supera los 10 MB"); return; }
    const b64 = await toBase64(file);
    setQForm(f => ({ ...f, pdf: file, pdfB64: b64 }));
  }

  async function handleReceiptFile(e) {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error("El comprobante supera los 10 MB"); return; }
    const b64 = await toBase64(file);
    setPayForm(f => ({ ...f, receipt: file, receiptB64: b64 }));
  }

  if (loading) return <div className="p-6 text-slate-400">Cargando...</div>;
  if (!req) return <div className="p-6 text-red-500">Solicitud no encontrada</div>;

  const approvedQuotation = req.quotations?.find(q => q.id === req.quotation_approved_id);
  const currentStatusIdx = TIMELINE_STEPS.indexOf(req.status);

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate("/ops/mantenimientos")} className="p-2 rounded-lg hover:bg-slate-100 text-slate-500">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-xl font-extrabold text-slate-900">{req.request_number}</h1>
              <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${STATUS_COLORS[req.status] || "bg-slate-100 text-slate-600"}`}>
                {STATUS_LABELS[req.status] || req.status}
              </span>
            </div>
            <p className="text-sm text-slate-500 mt-0.5">{req.title}</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Columna izquierda — info */}
        <div className="lg:col-span-2 space-y-4">
          {/* Info básica */}
          <SectionCard title="Informacion de la solicitud">
            <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
              <div>
                <span className="text-slate-500">Numero</span>
                <p className="font-mono font-semibold text-slate-800">{req.request_number}</p>
              </div>
              <div>
                <span className="text-slate-500">Sucursal</span>
                <p className="font-semibold text-slate-800">{req.branch_name || req.branch_id}</p>
              </div>
              <div>
                <span className="text-slate-500">Categoria</span>
                <p className="font-semibold text-slate-800">{req.category}</p>
              </div>
              <div>
                <span className="text-slate-500">Prioridad</span>
                <p className="font-semibold text-slate-800">{req.priority}</p>
              </div>
              <div>
                <span className="text-slate-500">Solicitante</span>
                <p className="font-semibold text-slate-800">{req.requested_by_name || req.requested_by_user_id}</p>
              </div>
              <div>
                <span className="text-slate-500">Fecha</span>
                <p className="font-semibold text-slate-800">{fmtDate(req.requested_at)}</p>
              </div>
              {req.estimated_cost_mxn != null && (
                <div>
                  <span className="text-slate-500">Costo estimado</span>
                  <p className="font-semibold text-slate-800">{fmtMXN(req.estimated_cost_mxn)}</p>
                </div>
              )}
            </div>
          </SectionCard>

          {/* Descripcion + fotos */}
          <SectionCard title="Problema reportado">
            <p className="text-sm text-slate-700 mb-3">{req.description}</p>
            {req.problem_photo_storage_paths?.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {req.problem_photo_storage_paths.map((_, idx) => (
                  <a
                    key={idx}
                    href={`${process.env.REACT_APP_BACKEND_URL}/api/ops/maintenance/files/${req.id}/photo/${idx}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`mnt-photo-link-${idx}`}
                    className="flex items-center gap-1.5 text-xs text-blue-600 hover:text-blue-800 bg-blue-50 px-2.5 py-1 rounded-lg border border-blue-200 font-medium"
                  >
                    <FileText className="w-3.5 h-3.5" />
                    Foto {idx + 1}
                  </a>
                ))}
              </div>
            )}
          </SectionCard>

          {/* Cotizaciones */}
          {(req.quotations?.length > 0 || ["cotizando", "cotizaciones_subidas"].includes(req.status)) && (
            <SectionCard title="Cotizaciones">
              <div className="space-y-3">
                {req.quotations?.map(q => (
                  <div
                    key={q.id}
                    data-testid={`mnt-quotation-${q.id}`}
                    className={`flex items-center justify-between p-3 rounded-xl border ${q.id === req.quotation_approved_id ? "border-green-400 bg-green-50" : "border-slate-200 bg-slate-50"}`}
                  >
                    <div>
                      <p className="text-sm font-semibold text-slate-800">{q.supplier_name}</p>
                      <p className="text-xs text-slate-500">Vigente hasta: {q.valid_until}</p>
                      {q.notes && <p className="text-xs text-slate-500 italic">{q.notes}</p>}
                    </div>
                    <div className="flex items-center gap-3">
                      <p className="text-base font-bold text-slate-900">{fmtMXN(q.amount_mxn)}</p>
                      {q.id === req.quotation_approved_id && (
                        <CheckCircle2 className="w-5 h-5 text-green-600" />
                      )}
                      <a
                        href={`${process.env.REACT_APP_BACKEND_URL}/api/ops/maintenance/files/${req.id}/quotation/${q.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"
                      >
                        <Download className="w-3.5 h-3.5" />
                        PDF
                      </a>
                      {isManager && ["cotizando", "cotizaciones_subidas"].includes(req.status) && (
                        <button
                          data-testid={`mnt-remove-quotation-${q.id}`}
                          onClick={() => doAction(`quotations/${q.id}`, {})}
                          className="text-xs text-red-500 hover:text-red-700 flex items-center gap-1"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
                {req.quotations?.length === 0 && (
                  <p className="text-sm text-slate-400">Sin cotizaciones aun</p>
                )}
              </div>
            </SectionCard>
          )}

          {/* Pago info */}
          {["pago_solicitado","pago_aprobado","en_tesoreria","completada"].includes(req.status) && (
            <SectionCard title="Informacion de pago">
              <div className="text-sm space-y-1.5">
                {req.payment_requested_at && (
                  <p><span className="text-slate-500">Pago solicitado:</span> <span className="font-semibold">{fmtDate(req.payment_requested_at)}</span></p>
                )}
                {req.payment_request_notes && (
                  <p><span className="text-slate-500">Notas:</span> {req.payment_request_notes}</p>
                )}
                {req.payment_approved_at && (
                  <p><span className="text-slate-500">Aprobado por Direccion:</span> <span className="font-semibold">{req.payment_approved_by_name}</span> — {fmtDate(req.payment_approved_at)}</p>
                )}
                {req.payment_executed_at && (
                  <>
                    <p><span className="text-slate-500">Pagado el:</span> <span className="font-semibold">{fmtDate(req.payment_executed_at)}</span></p>
                    <p><span className="text-slate-500">Metodo:</span> {req.payment_method}</p>
                    {req.payment_reference && <p><span className="text-slate-500">Referencia:</span> {req.payment_reference}</p>}
                    {req.payment_receipt_storage_path && (
                      <a
                        href={`${process.env.REACT_APP_BACKEND_URL}/api/ops/maintenance/files/${req.id}/receipt`}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="mnt-download-receipt"
                        className="inline-flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-800 font-medium mt-1"
                      >
                        <Download className="w-4 h-4" />
                        Descargar comprobante
                      </a>
                    )}
                    <p className="text-xs text-slate-400">
                      Enviado al proveedor: {req.payment_receipt_sent_to_supplier ? "Si" : "No"}
                    </p>
                  </>
                )}
              </div>
            </SectionCard>
          )}

          {/* Rechazo / cancelacion */}
          {["rechazada", "cancelada"].includes(req.status) && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm">
              <p className="font-bold text-red-700 mb-1">{req.status === "rechazada" ? "Rechazada" : "Cancelada"}</p>
              {req.rejected_at_step && (
                <p className="text-slate-600">Paso al rechazar: <span className="font-medium">{STATUS_LABELS[req.rejected_at_step] || req.rejected_at_step}</span></p>
              )}
              <p className="text-slate-700 mt-1">{req.rejection_reason || req.cancellation_reason}</p>
              <p className="text-xs text-slate-400 mt-1">{fmtDate(req.rejected_at || req.cancelled_at)}</p>
            </div>
          )}
        </div>

        {/* Columna derecha — timeline + acciones */}
        <div className="space-y-4">
          {/* Timeline */}
          <SectionCard title="Estado del proceso">
            <div className="space-y-1.5">
              {TIMELINE_STEPS.map((step, idx) => {
                const done = currentStatusIdx > idx || req.status === step;
                const active = req.status === step;
                const closed = ["rechazada", "cancelada"].includes(req.status);
                return (
                  <div key={step} className="flex items-center gap-2">
                    <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
                      closed && active ? "bg-red-500" :
                      done ? "bg-green-500" :
                      active ? "bg-yellow-500" :
                      "bg-slate-200"
                    }`} />
                    <span className={`text-xs ${active ? "font-bold text-slate-900" : done ? "text-green-700" : "text-slate-400"}`}>
                      {STATUS_LABELS[step]}
                    </span>
                  </div>
                );
              })}
            </div>
          </SectionCard>

          {/* Acciones */}
          <div className="space-y-2">
            {/* Carlos: tomar solicitud */}
            {isManager && req.status === "solicitada" && (
              <button
                data-testid="mnt-claim-btn"
                onClick={() => doAction("claim")}
                disabled={actionLoading}
                className="w-full py-2.5 bg-[#FFDF00] hover:bg-yellow-400 text-slate-900 font-semibold rounded-xl text-sm transition-colors"
              >
                Tomar y buscar cotizaciones
              </button>
            )}

            {/* Carlos: agregar cotizacion */}
            {isManager && ["cotizando", "cotizaciones_subidas"].includes(req.status) && (
              <button
                data-testid="mnt-add-quotation-btn"
                onClick={() => setShowAddQuotation(true)}
                className="w-full py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl text-sm transition-colors flex items-center justify-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Agregar cotizacion
              </button>
            )}

            {/* Dirección: aprobar cotizacion */}
            {isApprover && req.status === "cotizaciones_subidas" && req.quotations?.length > 0 && (
              <button
                data-testid="mnt-approve-quotation-btn"
                onClick={() => setShowApproveQuotation(true)}
                className="w-full py-2.5 bg-purple-600 hover:bg-purple-700 text-white font-semibold rounded-xl text-sm transition-colors"
              >
                Aprobar cotizacion
              </button>
            )}

            {/* Carlos: solicitar pago */}
            {isManager && req.status === "cotizacion_aprobada" && (
              <button
                data-testid="mnt-request-payment-btn"
                onClick={() => doAction("request-payment", {})}
                disabled={actionLoading}
                className="w-full py-2.5 bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-xl text-sm transition-colors"
              >
                Solicitar pago
              </button>
            )}

            {/* Dirección: aprobar pago */}
            {isApprover && req.status === "pago_solicitado" && (
              <button
                data-testid="mnt-approve-payment-btn"
                onClick={() => doAction("approve-payment", {})}
                disabled={actionLoading}
                className="w-full py-2.5 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-xl text-sm transition-colors"
              >
                Aprobar pago
              </button>
            )}

            {/* Tesorería: tomar para ejecutar */}
            {isTreasury && req.status === "pago_aprobado" && (
              <button
                data-testid="mnt-treasury-claim-btn"
                onClick={() => doAction("treasury-claim", {})}
                disabled={actionLoading}
                className="w-full py-2.5 bg-teal-600 hover:bg-teal-700 text-white font-semibold rounded-xl text-sm transition-colors"
              >
                Tomar para ejecutar
              </button>
            )}

            {/* Tesorería: marcar pagado */}
            {isTreasury && req.status === "en_tesoreria" && (
              <button
                data-testid="mnt-complete-payment-btn"
                onClick={() => setShowCompletePayment(true)}
                className="w-full py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-xl text-sm transition-colors"
              >
                Marcar como pagado
              </button>
            )}

            {/* Solicitante: cancelar (solo en solicitada) */}
            {isRequester && req.status === "solicitada" && (
              <button
                data-testid="mnt-cancel-btn"
                onClick={() => setShowCancel(true)}
                className="w-full py-2.5 border border-slate-200 text-slate-600 hover:bg-red-50 hover:border-red-300 font-semibold rounded-xl text-sm transition-colors"
              >
                Cancelar solicitud
              </button>
            )}

            {/* Rechazar */}
            {!["completada", "rechazada", "cancelada"].includes(req.status) &&
              (isManager || isApprover || isTreasury) && (
              <button
                data-testid="mnt-reject-btn"
                onClick={() => setShowReject(true)}
                className="w-full py-2.5 border border-red-200 text-red-600 hover:bg-red-50 font-semibold rounded-xl text-sm transition-colors"
              >
                Rechazar solicitud
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Modal: Agregar cotizacion */}
      {showAddQuotation && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md p-6 space-y-4" data-testid="mnt-add-quotation-modal">
            <div className="flex items-center justify-between">
              <h3 className="font-bold text-slate-900">Agregar cotizacion</h3>
              <button onClick={() => setShowAddQuotation(false)}><X className="w-5 h-5 text-slate-400" /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium text-slate-700 block mb-1">Proveedor *</label>
                <select
                  data-testid="mnt-q-supplier"
                  value={qForm.supplier_id}
                  onChange={e => setQForm(f => ({ ...f, supplier_id: e.target.value }))}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                >
                  <option value="">Seleccionar proveedor...</option>
                  {suppliers.map(s => <option key={s.id} value={s.id}>{s.name} ({s.specialty})</option>)}
                </select>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 block mb-1">Monto MXN *</label>
                <input
                  data-testid="mnt-q-amount"
                  type="number"
                  min={0}
                  value={qForm.amount_mxn}
                  onChange={e => setQForm(f => ({ ...f, amount_mxn: e.target.value }))}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 block mb-1">Vigencia *</label>
                <input
                  data-testid="mnt-q-valid-until"
                  type="date"
                  value={qForm.valid_until}
                  onChange={e => setQForm(f => ({ ...f, valid_until: e.target.value }))}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 block mb-1">PDF de la cotizacion *</label>
                <input
                  data-testid="mnt-q-pdf"
                  type="file"
                  accept="application/pdf"
                  onChange={handleQPdf}
                  className="w-full text-sm text-slate-500"
                />
                {qForm.pdf && <p className="text-xs text-green-600 mt-1">{qForm.pdf.name}</p>}
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 block mb-1">Notas (opcional)</label>
                <textarea
                  rows={2}
                  value={qForm.notes}
                  onChange={e => setQForm(f => ({ ...f, notes: e.target.value }))}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm resize-none"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <button onClick={() => setShowAddQuotation(false)} className="flex-1 py-2.5 border border-slate-200 rounded-xl text-sm font-medium text-slate-600">Cancelar</button>
              <button
                data-testid="mnt-q-submit"
                onClick={submitQuotation}
                disabled={actionLoading}
                className="flex-1 py-2.5 bg-[#FFDF00] hover:bg-yellow-400 text-slate-900 font-semibold rounded-xl text-sm"
              >
                {actionLoading ? "Subiendo..." : "Agregar"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Aprobar cotizacion */}
      {showApproveQuotation && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md p-6 space-y-4" data-testid="mnt-approve-quotation-modal">
            <div className="flex items-center justify-between">
              <h3 className="font-bold text-slate-900">Aprobar cotizacion</h3>
              <button onClick={() => setShowApproveQuotation(false)}><X className="w-5 h-5 text-slate-400" /></button>
            </div>
            <div className="space-y-3">
              {req.quotations?.map(q => (
                <label key={q.id} className={`flex items-center justify-between p-3 rounded-xl border cursor-pointer ${approveQId === q.id ? "border-yellow-400 bg-yellow-50" : "border-slate-200"}`}>
                  <div className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="quotation"
                      value={q.id}
                      checked={approveQId === q.id}
                      onChange={() => setApproveQId(q.id)}
                    />
                    <div>
                      <p className="text-sm font-medium">{q.supplier_name}</p>
                      <p className="text-xs text-slate-500">{fmtMXN(q.amount_mxn)}</p>
                    </div>
                  </div>
                </label>
              ))}
              <textarea
                rows={2}
                value={approveNotes}
                onChange={e => setApproveNotes(e.target.value)}
                placeholder="Notas (opcional)"
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm resize-none"
              />
            </div>
            <div className="flex gap-2">
              <button onClick={() => setShowApproveQuotation(false)} className="flex-1 py-2.5 border border-slate-200 rounded-xl text-sm font-medium text-slate-600">Cancelar</button>
              <button
                data-testid="mnt-approve-q-submit"
                onClick={submitApproveQuotation}
                disabled={actionLoading || !approveQId}
                className="flex-1 py-2.5 bg-purple-600 hover:bg-purple-700 text-white font-semibold rounded-xl text-sm"
              >
                Aprobar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Completar pago */}
      {showCompletePayment && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md p-6 space-y-4" data-testid="mnt-complete-payment-modal">
            <div className="flex items-center justify-between">
              <h3 className="font-bold text-slate-900">Confirmar pago</h3>
              <button onClick={() => setShowCompletePayment(false)}><X className="w-5 h-5 text-slate-400" /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium text-slate-700 block mb-1">Metodo de pago *</label>
                <select
                  data-testid="mnt-pay-method"
                  value={payForm.method}
                  onChange={e => setPayForm(f => ({ ...f, method: e.target.value }))}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                >
                  <option value="transferencia">Transferencia</option>
                  <option value="efectivo">Efectivo</option>
                  <option value="cheque">Cheque</option>
                </select>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 block mb-1">Referencia (opcional)</label>
                <input
                  data-testid="mnt-pay-reference"
                  type="text"
                  value={payForm.reference}
                  onChange={e => setPayForm(f => ({ ...f, reference: e.target.value }))}
                  placeholder="Numero de operacion BBVA, etc."
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 block mb-1">Comprobante de pago *</label>
                <input
                  data-testid="mnt-pay-receipt"
                  type="file"
                  accept="image/*,application/pdf"
                  onChange={handleReceiptFile}
                  className="w-full text-sm text-slate-500"
                />
                {payForm.receipt && <p className="text-xs text-green-600 mt-1">{payForm.receipt.name}</p>}
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  data-testid="mnt-pay-send"
                  checked={payForm.send}
                  onChange={e => setPayForm(f => ({ ...f, send: e.target.checked }))}
                />
                <span className="text-sm text-slate-700">Enviar comprobante al proveedor por email</span>
              </label>
            </div>
            <div className="flex gap-2">
              <button onClick={() => setShowCompletePayment(false)} className="flex-1 py-2.5 border border-slate-200 rounded-xl text-sm font-medium text-slate-600">Cancelar</button>
              <button
                data-testid="mnt-complete-payment-submit"
                onClick={submitCompletePayment}
                disabled={actionLoading || !payForm.receiptB64}
                className="flex-1 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-xl text-sm"
              >
                {actionLoading ? "Procesando..." : "Confirmar pago"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Rechazar */}
      {showReject && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md p-6 space-y-4" data-testid="mnt-reject-modal">
            <div className="flex items-center justify-between">
              <h3 className="font-bold text-red-700">Rechazar solicitud</h3>
              <button onClick={() => setShowReject(false)}><X className="w-5 h-5 text-slate-400" /></button>
            </div>
            <textarea
              data-testid="mnt-reject-reason"
              rows={3}
              value={rejectReason}
              onChange={e => setRejectReason(e.target.value)}
              placeholder="Motivo del rechazo (minimo 10 caracteres)..."
              className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-red-300"
            />
            <div className="flex gap-2">
              <button onClick={() => setShowReject(false)} className="flex-1 py-2.5 border border-slate-200 rounded-xl text-sm font-medium text-slate-600">Cancelar</button>
              <button
                data-testid="mnt-reject-submit"
                onClick={async () => {
                  if (rejectReason.trim().length < 10) { toast.error("El motivo debe tener al menos 10 caracteres"); return; }
                  await doAction("reject", { reason: rejectReason.trim() });
                  setShowReject(false);
                }}
                disabled={actionLoading}
                className="flex-1 py-2.5 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-xl text-sm"
              >
                Rechazar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Cancelar */}
      {showCancel && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md p-6 space-y-4" data-testid="mnt-cancel-modal">
            <div className="flex items-center justify-between">
              <h3 className="font-bold text-slate-900">Cancelar solicitud</h3>
              <button onClick={() => setShowCancel(false)}><X className="w-5 h-5 text-slate-400" /></button>
            </div>
            <textarea
              data-testid="mnt-cancel-reason"
              rows={3}
              value={cancelReason}
              onChange={e => setCancelReason(e.target.value)}
              placeholder="Motivo de cancelacion..."
              className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-slate-300"
            />
            <div className="flex gap-2">
              <button onClick={() => setShowCancel(false)} className="flex-1 py-2.5 border border-slate-200 rounded-xl text-sm font-medium text-slate-600">Atras</button>
              <button
                data-testid="mnt-cancel-submit"
                onClick={async () => {
                  if (cancelReason.trim().length < 5) { toast.error("El motivo es muy corto"); return; }
                  await doAction("cancel", { reason: cancelReason.trim() });
                  setShowCancel(false);
                }}
                disabled={actionLoading}
                className="flex-1 py-2.5 bg-slate-800 hover:bg-slate-900 text-white font-semibold rounded-xl text-sm"
              >
                Confirmar cancelacion
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
